import { useRouter } from 'next/router';
import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getAllLinksForLocale } from '~/lib/contentful-cache';
import { ContentfulQueryKeys, LinkMap } from '@oresundsbron/api';

export function useLinksV2() {
  const queryClient = useQueryClient();
  const { locale = 'en', isPreview } = useRouter();

  const queryKey = ContentfulQueryKeys.LinksV2({
    locales: [locale],
    preview: Boolean(isPreview),
  });

  const { data: linksForLocale } = useQuery<LinkMap>({
    queryKey,
    queryFn: () => getAllLinksForLocale(locale, Boolean(isPreview)),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    initialData: () =>
      queryClient.getQueryData(queryKey) as LinkMap | undefined,
  });

  const getLink = useCallback(
    (id: string) => {
      if (linksForLocale && id in linksForLocale) return linksForLocale[id];
      return Object.values(linksForLocale || {}).find(
        (link) => link.id === id || link.ref === id || link.path.endsWith(id)
      );
    },
    [linksForLocale]
  );

  const findLink = useCallback(
    (path: string) =>
      Object.values(linksForLocale || {}).find((l) => l.path === path),
    [linksForLocale]
  );

  const findLinkByCodeRef = useCallback(
    (ref: string) =>
      Object.values(linksForLocale || {}).find((l) => l.ref === ref),
    [linksForLocale]
  );

  const findPageId = useCallback(
    (path: string) => {
      const entry = Object.entries(linksForLocale || {}).find(
        ([, l]) => l.path === path
      );
      return entry ? entry[0] : undefined;
    },
    [linksForLocale]
  );

  return {
    links: linksForLocale,
    getLink,
    findLink,
    findLinkByCodeRef,
    findPageId,
  };
}
